/**
 * ScanHudPreview — the real capture of the Nexus Dex HUD parked in place, with
 * its legend laid over the shot it comes from.
 *
 * ONE component, two surfaces (2026-08-31 owner ask): the "How to scan" guide
 * shows it above each page's steps, and the scan menu shows it in the dead
 * backdrop space above the sheet. They were briefly two copies of the same
 * markup, which is how a legend goes stale on one surface and not the other.
 *
 * Two variants, and the caller does not choose between them by hand — it says
 * WHICH SCAN it is and gets the matching picture:
 *
 *   "live"    the Pokédex grid scan. The HUD sits TOP-RIGHT over the header,
 *             and the legend is the HUD's own two number-chip formats blown
 *             up: a light chip is newly added, a dark green chip is
 *             already registered, and (2026-09-24) the numbers read as not
 *             caught: orange-red for a silhouette, red for a blank card.
 *             Those enlarged chips ARE the legend, so no surface repeats
 *             them underneath.
 *   "pokemon" the individual-Pokémon scan. Since the portrait HUD
 *             (2026-09-08) the window turns into a narrow column top-right
 *             on a Pokémon page and clears the CP and the IV bars on its
 *             own, so the lesson is "leave it where it is": the capture shows
 *             the column beside a readable CP with the appraised card in it.
 *             (Until then the lesson was to park the landscape card
 *             bottom-right, because anywhere near the top covered the CP.)
 *
 * The captures live in client/public/tutorial (~100KB each, downscaled).
 *
 * PER PLATFORM (2026-09-03 owner ask): the Android HUD is a dark overlay of
 * its own, not the iOS mint card you can drag, so an Android trainer opening
 * the guide saw a picture of a HUD they do not have. Each kind now carries an
 * Android capture and caption of its own; web and iOS share the iOS set. The
 * chip legend is the same language on both.
 *
 * 2026-09-22: the Android HUD became small docked pieces (a bubble or tab
 * at the left edge, a destiny badge at the right, full card on a tap) and the
 * grid scan's dock sits top-right with the last twelve numbers under the
 * bubble. The captions describe that;
 * the two Android captures still show the earlier top-of-screen panel and
 * are owed a re-shoot on a device (overlays never appear in a screencap, so
 * they are photographed).
 */
import type { ReactNode } from "react";
import { getPlatform, type Platform } from "@/lib/platform";

export type ScanHudPreviewKind = "live" | "pokemon";

/** HUD number-chip colours: the shared visual language for scan results.
 * Light chip + dark text = NEWLY registered; dark green chip + white text =
 * ALREADY registered; orange-red = a silhouette (seen, not caught); red = a
 * blank card (not caught). Literal HSL on purpose: these mirror the native
 * HUD chips (NexPip3D.swift / NexPip3D.java), not the app theme. */
export const CHIP_NEW_BG = "hsl(140 40% 92%)";
export const CHIP_NEW_TEXT = "hsl(152 45% 22%)";
export const CHIP_SEEN_BG = "hsl(152 42% 24%)";
export const CHIP_SEEN_TEXT = "hsl(0 0% 100%)";
export const CHIP_SILHOUETTE_BG = "hsl(15 87% 55%)";
export const CHIP_MISSING_BG = "hsl(0 65% 50%)";

/**
 * The captures are LIGHT at the bottom, so a legend laid over them cannot rely
 * on the image for contrast: the scrim carries the white text on its own,
 * opaque at the base and faded out by the top.
 */
const SCRIM =
  "linear-gradient(to top, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.82) 42%, rgba(0,0,0,0) 100%)";

type Capture = {
  src: string;
  alt: string;
  /** The Pokémon shot's caption: the position lesson, per platform. */
  caption: { lead: string; detail: string };
};

const SOURCE: Record<ScanHudPreviewKind, Record<"ios" | "android", Capture>> = {
  live: {
    ios: {
      src: "/tutorial/scan-hud-pokedex.jpg",
      alt: "The Pokémon GO 100% Pokédex grid with the Nexus Dex HUD parked in the top-right corner, mirroring the four rows on screen: green chips for caught entries and red chips for the ones not caught",
      caption: { lead: "", detail: "" },
    },
    android: {
      src: "/tutorial/scan-hud-pokedex-android.jpg",
      alt: "The Pokémon GO Pokédex grid on Android with the Nexus Dex HUD bubble docked in the top-right corner over the Pokédex header showing the scroll arrow, and the last twelve dex numbers it read as chips under it, four wide",
      caption: { lead: "", detail: "" },
    },
  },
  pokemon: {
    ios: {
      src: "/tutorial/scan-hud-pokemon.jpg",
      alt: "One of your own Pokémon open in Pokémon GO with the Nexus Dex HUD standing as a narrow column top-right, the CP readable beside it, showing the Pokémon's name, CP, IVs, best league and its art",
      // Reassuring, not prescriptive (2026-09-08): the portrait HUD keeps
      // itself clear of the CP, so nothing needs positioning.
      caption: { lead: "The HUD stays put", detail: "Top-right, clear of the CP and the IV bars." },
    },
    android: {
      src: "/tutorial/scan-hud-pokemon-android.jpg",
      alt: "One of your own Pokémon open in Pokémon GO on Android: a small Nexus Dex HUD tab at the left edge beside the art with the name and IVs, its border in the appraisal colour, and a badge at the right edge over the team leader showing what it becomes with its league ball and standing",
      // Android's HUD is two small docked pieces: the facts at the left edge,
      // the destiny picture at the right. The lesson is that the page stays
      // readable, both slide to wherever there is room, and either opens the
      // full card on a tap.
      caption: { lead: "The HUD docks at the edges on Android", detail: "Facts on the left, what it becomes on the right. Tap either for the full card." },
    },
  },
};

export function ScanHudPreview({
  kind,
  testId,
  legendTestId,
  className,
  imgClassName,
  imgStyle,
  platform,
}: {
  kind: ScanHudPreviewKind;
  /** Which platform's capture to show. Defaults to the running platform;
   *  web shares the iOS set. Tests pass it explicitly. */
  platform?: Platform;
  /** Testid for the framing element. */
  testId: string;
  /** Testid for the legend overlay. Defaults to `${testId}-legend`; the guide
   *  passes its own because its names predate this component. */
  legendTestId?: string;
  className?: string;
  /** The scan menu constrains the height to whatever space it has. */
  imgClassName?: string;
  imgStyle?: React.CSSProperties;
}) {
  const legendId = legendTestId ?? `${testId}-legend`;
  const resolved = platform ?? getPlatform();
  const { src, alt, caption } = SOURCE[kind][resolved === "android" ? "android" : "ios"];
  return (
    <div
      className={`relative rounded-xl overflow-hidden ${className ?? ""}`}
      style={{ border: "1px solid var(--accent-fill-glow-subtle)" }}
      data-testid={testId}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full block ${imgClassName ?? ""}`}
        style={imgStyle}
      />
      {kind === "live" ? (
        <div
          className="absolute inset-x-0 bottom-0 grid grid-cols-4 items-start gap-1.5 px-2 pt-14 pb-3"
          style={{ background: SCRIM }}
          data-testid={legendId}
        >
          <BigChipLegend bg={CHIP_NEW_BG} text={CHIP_NEW_TEXT} chip="#045" label="Newly added" />
          <BigChipLegend bg={CHIP_SEEN_BG} text={CHIP_SEEN_TEXT} chip="#028" label="Already registered" />
          <BigChipLegend bg={CHIP_SILHOUETTE_BG} text={CHIP_SEEN_TEXT} chip="#046" label="Seen, not caught" />
          <BigChipLegend bg={CHIP_MISSING_BG} text={CHIP_SEEN_TEXT} chip="#047" label="Not caught" />
        </div>
      ) : (
        <div
          className="absolute inset-x-0 bottom-0 px-3 pt-12 pb-2.5 text-left"
          style={{ background: SCRIM }}
          data-testid={legendId}
        >
          <p className="text-[11px] font-bold leading-snug" style={{ color: "hsl(0 0% 100%)" }}>
            {caption.lead}
          </p>
          <p className="text-[10px] leading-snug" style={{ color: "hsl(0 0% 100% / 0.75)" }}>
            {caption.detail}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Oversized legend entry for the HUD's number chips, sized to be read while
 * the chip it names is visible in the same picture. Renders the actual chip
 * (bg + text colour), not a swatch, so the mapping is unmistakable.
 */
function BigChipLegend({
  bg,
  text,
  chip,
  label,
}: {
  bg: string;
  text: string;
  chip: string;
  label: string;
}): ReactNode {
  return (
    <span className="flex flex-col items-center gap-1 text-center">
      <span
        className="inline-flex items-center rounded-lg px-2 py-1.5 text-[17px] font-black tabular-nums leading-none"
        style={{ background: bg, color: text }}
      >
        {chip}
      </span>
      <span className="text-[10px] font-bold leading-tight" style={{ color: "hsl(0 0% 100%)" }}>
        {label}
      </span>
    </span>
  );
}
